import { useEffect, useRef, useState } from 'react';
import {
  useCustomMutation,
  useInvalidate,
  useNotification,
  useTranslate,
} from '@refinedev/core';
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Flex,
  Modal,
  Spin,
  Typography,
  theme,
} from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { statusTone } from '@/components/common/statusTone';
import type { ModStatus } from '@/api/generated/schemas';
import { usePublicationTrees } from '@/hooks/user_catalog/usePublicationTrees';
import { isAxiosError } from 'axios';
import { extractBackendMessage, extractDetailItems } from '@/utils/apiError';
import {
  buildEnablePlan,
  buildRunSteps,
  bulkPublicationItems,
  defaultInclude,
  eligibleTrees,
  groupItemErrors,
  isFormatKind,
  REQUIRED_ABOVE,
  unlicensed,
} from './enablePlan';
import type {
  BlockedItem,
  DependentGroup,
  DependentKind,
  FormatKind,
  RunStep,
  RunStepKind,
} from './enablePlan';
import { RunProgress } from './RunProgress';
import type { StepProgress } from './RunProgress';

const { Text, Title } = Typography;

const PUBLICATIONS_BULK_ROUTE = 'user_catalog/publications/update_status';

const BULK_ROUTES: Record<Exclude<DependentKind, FormatKind>, string> = {
  publishers: 'user_catalog/publishers/update_status',
  authors: 'user_catalog/authors/update_status',
  lectors: 'user_catalog/lectors/update_status',
};

interface PublicationModStatusModalProps {
  ids: number[];
  /** Formats the action is scoped to; empty = all formats of the publications. */
  productIds?: number[];
  /** Target moderation status for the publications. */
  status: ModStatus;
  open: boolean;
  onClose: () => void;
  /** Every step that finished — callers refetch views that show the status. */
  onStepDone?: () => void;
  /** The whole run finished. */
  onDone?: () => void;
}

/** A chip row item; `usedBy` is shown only for dependents. */
type ChipItem = Pick<BlockedItem, 'id' | 'name' | 'href' | 'mod_status'> & {
  usedBy?: number;
};

/** `● Name  n ↗` — an object with its current status as the dot colour. */
const ItemChip = ({ item }: { item: ChipItem }) => {
  const { token } = theme.useToken();
  const dotColor = {
    success: token.colorSuccess,
    warning: token.colorWarning,
    error: token.colorError,
  }[statusTone(item.mod_status)];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: token.marginXS,
        padding: `${token.paddingXXS}px ${token.paddingSM}px`,
        borderRadius: token.borderRadiusLG * 2,
        background: token.colorBgContainer,
        border: `${token.lineWidth}px solid ${token.colorBorderSecondary}`,
      }}
    >
      <span style={{ color: dotColor, fontSize: token.fontSizeSM - 3 }}>●</span>
      <span>{item.name}</span>
      {item.usedBy != null && <Text type="secondary">{item.usedBy}</Text>}
      <Typography.Link
        href={item.href}
        target="_blank"
        rel="noopener"
        aria-label={item.name}
        style={{ display: 'inline-flex' }}
      >
        <ExportOutlined />
      </Typography.Link>
    </span>
  );
};

interface GroupCardProps {
  title: string;
  items: ChipItem[];
  checked: boolean;
  /** Always included — e.g. the publications themselves, or their publisher. */
  locked?: boolean;
  onToggle?: (checked: boolean) => void;
  description: string;
  /** Start with the chips visible (default: yes). */
  defaultExpanded?: boolean;
}

/** Collapsible card: checkbox, title with count, description, chips. */
const GroupCard = ({
  title,
  items,
  checked,
  locked,
  onToggle,
  description,
  defaultExpanded,
}: GroupCardProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const [expanded, setExpanded] = useState(defaultExpanded ?? true);
  const k = (key: string, opts?: Record<string, unknown>) =>
    t(`user_catalog.publications.enable_modal.${key}`, opts);

  return (
    <div
      style={{
        border: `${token.lineWidth}px solid ${checked ? token.colorPrimaryBorder : token.colorBorderSecondary}`,
        background: checked ? token.colorPrimaryBg : token.colorFillQuaternary,
        borderRadius: token.borderRadiusLG,
        padding: token.padding,
      }}
    >
      <Flex align="flex-start" gap={token.marginSM}>
        <Checkbox
          checked={checked}
          disabled={locked}
          aria-label={title}
          onChange={(e) => onToggle?.(e.target.checked)}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text strong>
            {title} ({items.length})
          </Text>
          <Text type="secondary" style={{ display: 'block' }}>
            {description}
          </Text>
        </div>
        <Button
          type="link"
          size="small"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? k('hide') : k('show', { count: items.length })}
        </Button>
      </Flex>
      {expanded && (
        <>
          <Divider dashed style={{ margin: `${token.marginSM}px 0` }} />
          <Flex gap={token.marginXS} wrap>
            {items.map((item) => (
              <ItemChip key={item.id} item={item} />
            ))}
          </Flex>
        </>
      )}
    </div>
  );
};

/**
 * Enabling a publication only makes it visible if its publisher, at least
 * one product, and its people are enabled too. This modal shows what is
 * blocking and enables those dependents in the same go.
 */
export const PublicationModStatusModal = ({
  ids,
  productIds = [],
  status,
  open,
  onClose,
  onStepDone,
  onDone,
}: PublicationModStatusModalProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const { open: notify } = useNotification();
  const invalidate = useInvalidate();
  const enabling = status === 'enabled';
  // Formats selected → they are the subject; publications are their parents.
  const byProduct = productIds.length > 0;
  // The dependency tree is always shown: it carries the licence check and
  // resolves the publications behind selected formats.
  const {
    trees: batch,
    isLoading: loadingTree,
    isError: treeFailed,
    refetch: refetchTree,
  } = usePublicationTrees(ids, productIds, open);
  const loaded = batch?.trees ?? [];
  const plan = buildEnablePlan(loaded, status);
  // Hierarchy: publishers → publications → authors / lectors / formats.
  const visibleGroups = plan.groups;
  const blocked = enabling
    ? unlicensed(loaded)
    : { publications: [], products: [] };
  const eligible = eligibleTrees(loaded, status);
  // Locked: the selected formats (the subject) and, when enabling, the
  // levels above them — a publication cannot show under a disabled publisher.
  const lockedKinds: DependentKind[] = [
    ...(byProduct ? (['ebooks', 'audios'] as DependentKind[]) : []),
    ...(enabling ? REQUIRED_ABOVE : []),
  ];
  // Publications: the subject (locked) unless formats are; then required
  // above the formats when enabling, optional cascade otherwise.
  const publicationsLocked = !byProduct || enabling;
  // Only the user's own choices are stored; what is checked is derived from
  // them, so a refetch of the tree can never overwrite a selection.
  const [overrides, setOverrides] = useState<
    Partial<Record<DependentKind | 'publications', boolean>>
  >({});
  const defaults = defaultInclude(visibleGroups, status);
  const include = new Set(
    visibleGroups
      .map((g) => g.kind)
      .filter(
        (kind) =>
          lockedKinds.includes(kind) || (overrides[kind] ?? defaults.has(kind))
      )
  );
  const includePublications =
    publicationsLocked || (overrides.publications ?? false);
  const setOverride = (
    kind: DependentKind | 'publications',
    checked: boolean
  ) => setOverrides((prev) => ({ ...prev, [kind]: checked }));
  const setAllOverrides = (checked: boolean) =>
    setOverrides(
      Object.fromEntries([
        ...visibleGroups.map((g) => [g.kind, checked]),
        ['publications', checked],
      ])
    );
  // Select/deselect all is dumb on purpose: it flips every row, publications
  // included; the hierarchy locks simply hold the required ones in place.
  const allChecked =
    visibleGroups.every((g) => include.has(g.kind)) && includePublications;
  const anyOptional =
    visibleGroups.some((g) => !lockedKinds.includes(g.kind)) ||
    !publicationsLocked;
  const selectAll = () => setAllOverrides(true);
  const deselectAll = () => setAllOverrides(false);
  const changingIn = (kind: DependentKind) =>
    include.has(kind)
      ? (visibleGroups
          .find((g) => g.kind === kind)
          ?.items.filter((i) => i.mod_status !== status).length ?? 0)
      : 0;
  /**
   * What will actually be sent, per kind in hierarchy order: only objects
   * not yet at the target status, from the included groups.
   */
  const publicationChanges = includePublications
    ? eligible.filter((tr) => tr.publication.mod_status !== status).length
    : 0;
  const allChanges: { kind: DependentKind | 'publications'; count: number }[] =
    [
      { kind: 'publishers', count: changingIn('publishers') },
      { kind: 'publications', count: publicationChanges },
      { kind: 'authors', count: changingIn('authors') },
      { kind: 'lectors', count: changingIn('lectors') },
      { kind: 'ebooks', count: changingIn('ebooks') },
      { kind: 'audios', count: changingIn('audios') },
    ];
  const changes = allChanges.filter((c) => c.count > 0);
  const totalChanges = changes.reduce((n, c) => n + c.count, 0);

  const { mutateAsync: bulk } = useCustomMutation();
  // `null` = still configuring; otherwise the run is in progress or finished.
  const [steps, setSteps] = useState<StepProgress[] | null>(null);
  const abortRef = useRef(false);
  // Unmounting mid-run stops the queue: no more requests, and no state
  // updates from the request already in flight.
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );
  const running = steps?.some((s) => s.state === 'running') ?? false;
  // Failed or aborted: either way the run stopped with steps left to send.
  const stoppedStep = steps?.find(
    (s) => s.state === 'failed' || s.state === 'aborted'
  );
  const finished =
    steps != null &&
    !running &&
    steps.every((s) => s.state === 'done' || s.state === 'skipped');
  const enabledObjects =
    steps?.filter((s) => s.state === 'done').reduce((n, s) => n + s.count, 0) ??
    0;
  const plannedObjects = steps?.reduce((n, s) => n + s.count, 0) ?? 0;

  const k = (key: string, opts?: Record<string, unknown>) =>
    t(`user_catalog.publications.enable_modal.${key}`, opts);

  const groupDescription = (g: DependentGroup) => {
    if (!enabling)
      return isFormatKind(g.kind)
        ? k('cascade_products')
        : k('cascade_shared_warning');
    return g.kind === 'publishers'
      ? k('publisher_required', {
          count: g.items.length,
          publications: g.items.reduce((n, i) => n + i.usedBy, 0),
        })
      : k('required_by_selected');
  };

  const renderGroup = (g: DependentGroup) => (
    <GroupCard
      key={g.kind}
      title={k(`kinds.${g.kind}`)}
      items={g.items}
      checked={include.has(g.kind)}
      locked={lockedKinds.includes(g.kind)}
      description={groupDescription(g)}
      onToggle={(on) => setOverride(g.kind, on)}
    />
  );

  const runStep = async (step: RunStep) => {
    if (step.kind !== 'publications' && step.kind !== 'formats') {
      await bulk({
        url: BULK_ROUTES[step.kind],
        method: 'patch',
        values: { ids: step.ids, mod_status: status },
        successNotification: false,
        errorNotification: false,
      });
      return;
    }
    // One request for publications and formats alike: each item carries the
    // publication's new status (if any) and the formats changing with it.
    const items = bulkPublicationItems(
      eligible,
      include,
      status,
      includePublications
    );
    if (items.length === 0) return;
    await bulk({
      url: PUBLICATIONS_BULK_ROUTE,
      method: 'patch',
      values: { items },
      successNotification: false,
      errorNotification: false,
    });
  };

  const stepError = (
    step: RunStep,
    error: unknown
  ): Pick<StepProgress, 'error' | 'itemErrors'> => {
    if (step.kind === 'publications' || step.kind === 'formats') {
      const itemErrors = groupItemErrors(
        bulkPublicationItems(eligible, include, status, includePublications),
        eligible,
        extractDetailItems(
          isAxiosError(error) ? error.response?.data : undefined
        ),
        (kind) => t(`user_catalog.common.formats.${kind}`)
      );
      if (itemErrors.length > 0) return { itemErrors };
    }
    return { error: extractBackendMessage(error) };
  };

  const setState = (kind: RunStepKind, patch: Partial<StepProgress>) =>
    setSteps((prev) =>
      prev ? prev.map((s) => (s.kind === kind ? { ...s, ...patch } : s)) : prev
    );

  /**
   * Steps run one after another; a failure stops the run and leaves the
   * finished steps as they are, so retry only re-sends the failed kind.
   */
  const run = async (queue: StepProgress[]) => {
    abortRef.current = false;
    setSteps(queue);
    for (const step of queue) {
      if (step.state === 'done' || step.state === 'skipped') continue;
      if (abortRef.current || !mountedRef.current) return;
      if (step.count === 0) {
        // Everything in this step is already at the target — no request.
        setState(step.kind, { state: 'skipped' });
        continue;
      }
      setState(step.kind, {
        state: 'running',
        error: undefined,
        itemErrors: undefined,
      });
      try {
        await runStep(step);
        if (!mountedRef.current) return;
        setState(step.kind, { state: 'done' });
        // Finished steps are already persisted — let dependent views catch up.
        invalidate({
          resource: `user_catalog.${step.kind === 'formats' ? 'publications' : step.kind}`,
          invalidates: ['list', 'detail'],
        });
        onStepDone?.();
      } catch (error) {
        if (!mountedRef.current) return;
        setState(step.kind, { state: 'failed', ...stepError(step, error) });
        return;
      }
    }
    if (!mountedRef.current) return;
    // Each step already invalidated its own kind; nothing left to refetch here.
    notify?.({
      type: 'success',
      message: t('notifications.success'),
      // What was actually sent, not what was on screen.
      description: k(`success.${status}`, {
        count: publicationChanges,
        objects: totalChanges - publicationChanges,
      }),
    });
    onDone?.();
  };

  const start = () =>
    run(
      buildRunSteps(
        visibleGroups,
        include,
        // Publications already at the target status are shown, not sent.
        eligible
          .filter((tr) => tr.publication.mod_status !== status)
          .map((tr) => tr.publication.id),
        includePublications,
        status
      ).map((s) => ({
        ...s,
        state: 'queued' as const,
      }))
    );
  const retry = () =>
    steps &&
    run(
      steps.map((s) =>
        s.state === 'failed' || s.state === 'aborted'
          ? { ...s, state: 'queued', error: undefined }
          : s
      )
    );
  const abort = () => {
    abortRef.current = true;
    setSteps((prev) =>
      prev
        ? prev.map((s) =>
            // Not a failure: the steps were never sent, and retry re-queues them.
            s.state === 'queued' ? { ...s, state: 'aborted' } : s
          )
        : prev
    );
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={720}
      title={
        <div>
          <Title level={4} style={{ margin: 0 }}>
            {byProduct
              ? k(`title_products.${status}`, { count: productIds.length })
              : k(`title.${status}`, { count: ids.length })}
          </Title>
          {enabling && !loadingTree && plan.blockedPublications > 0 && (
            <Text type="secondary" style={{ fontWeight: 'normal' }}>
              {k('subtitle', { count: plan.blockedPublications })}
            </Text>
          )}
        </div>
      }
      closable={!running}
      keyboard={!running}
      maskClosable={false}
      footer={
        steps ? (
          <Flex align="center" gap={token.marginSM}>
            <Text
              type="secondary"
              // Flexible slot: long summaries wrap in place instead of
              // re-flowing the button row.
              style={{ flex: 1, minWidth: 0, textAlign: 'left' }}
            >
              {k(`run.progress.${status}`, {
                done: enabledObjects,
                total: plannedObjects,
              })}
            </Text>
            {/* Secondary slot: only while there is something to leave behind —
                a run to abort, or failed steps to walk away from. */}
            {running && <Button onClick={abort}>{k('run.abort')}</Button>}
            {stoppedStep && !running && (
              <Button onClick={onClose}>{t('actions.close')}</Button>
            )}
            {stoppedStep && !running ? (
              <Button type="primary" onClick={retry}>
                {k('run.retry')}
              </Button>
            ) : finished ? (
              // The run is over: the primary slot dismisses instead of
              // reporting — a dead "Gotowe" reads as a broken button.
              <Button type="primary" onClick={onClose}>
                {k('run.finished')}
              </Button>
            ) : (
              <Button type="primary" disabled loading={running}>
                {k(`run.in_progress.${status}`)}
              </Button>
            )}
          </Flex>
        ) : (
          <Flex align="center" gap={token.marginSM}>
            <Text
              type="secondary"
              // Flexible slot: long summaries wrap in place instead of
              // re-flowing the button row.
              style={{ flex: 1, minWidth: 0, textAlign: 'left' }}
            >
              {k(`footer_prefix.${status}`)}{' '}
              {changes.length === 0
                ? k('footer_nothing')
                : changes
                    .map((c) => `${k(`kinds.${c.kind}`)} ${c.count}`)
                    .join(' · ')}
            </Text>
            <Button onClick={onClose}>{t('actions.cancel')}</Button>
            <Button
              type="primary"
              disabled={totalChanges === 0 || loadingTree || treeFailed}
              onClick={start}
            >
              {k(`ok.${status}`, {
                count: totalChanges,
              })}
            </Button>
          </Flex>
        )
      }
    >
      {steps ? (
        <RunProgress steps={steps} status={status} />
      ) : loadingTree ? (
        <Spin />
      ) : treeFailed ? (
        // Without the tree there is nothing to configure — say so instead of
        // showing an empty list under a footer that claims "nothing to change".
        <Alert
          type="error"
          showIcon
          message={t('notifications.error')}
          action={
            <Button size="small" onClick={() => refetchTree()}>
              {t('actions.retry')}
            </Button>
          }
        />
      ) : (
        <Flex vertical gap={token.marginSM}>
          {blocked.publications.length + blocked.products.length > 0 && (
            <Alert
              type="error"
              showIcon
              message={k('unlicensed_title', {
                count: blocked.publications.length + blocked.products.length,
              })}
              description={
                <Flex vertical gap={token.marginXS}>
                  <Text type="secondary">{k('unlicensed_hint')}</Text>
                  <Flex gap={token.marginXS} wrap>
                    {[
                      ...blocked.publications.map((item) => ({
                        key: `pub-${item.id}`,
                        item,
                      })),
                      ...blocked.products.map((item) => ({
                        key: `prod-${item.id}`,
                        item,
                      })),
                    ].map(({ key, item }) => (
                      <ItemChip key={key} item={item} />
                    ))}
                  </Flex>
                </Flex>
              }
            />
          )}
          {visibleGroups.length > 0 && (
            <Flex align="center" justify="space-between">
              <Text
                type="secondary"
                strong
                style={{
                  fontSize: token.fontSizeSM,
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                }}
              >
                {k(enabling ? 'blocks_visibility' : 'cascade_header', {
                  count: plan.blockedObjects,
                })}
              </Text>
              {anyOptional && (
                <Button
                  type="link"
                  size="small"
                  onClick={allChecked ? deselectAll : selectAll}
                >
                  {allChecked ? k('deselect_all') : k('select_all')}
                </Button>
              )}
            </Flex>
          )}
          {/* Widest scope first: publishers, then the publications, then the rest. */}
          {visibleGroups
            .filter((g) => g.kind === 'publishers')
            .map(renderGroup)}
          {eligible.length > 0 && (
            <GroupCard
              title={k('kinds.publications')}
              description={k(
                byProduct
                  ? enabling
                    ? 'publications_required_by_formats'
                    : 'publications_cascade_warning'
                  : 'publications_selected'
              )}
              checked={includePublications}
              locked={publicationsLocked}
              onToggle={(on) => setOverride('publications', on)}
              items={eligible.map((tr) => ({
                id: tr.publication.id,
                name: tr.publication.name,
                mod_status: tr.publication.mod_status,
                href: `/user_catalog/publications/${tr.publication.id}`,
              }))}
            />
          )}
          {visibleGroups
            .filter((g) => g.kind !== 'publishers')
            .map(renderGroup)}
        </Flex>
      )}
    </Modal>
  );
};
